import manifest from '../data/content-manifest.generated.json';
import { DOCUMENT_ALIASES, SOURCE_ALIASES, rfcSpecUrl, REPO_URL } from '../data/nav';

export type Frontmatter = {
  title?: string; description?: string; minutes?: number; status?: string;
  date?: string; medium_url?: string; substack_url?: string; linkedin_url?: string;
  era?: string; iteration?: string; [key: string]: unknown;
};
export type ContentMetadata = {
  source: string; url: string; title: string; description: string; frontmatter: Frontmatter;
  ready: boolean; isJson: boolean; sourcePath: string; sourceRepository: string;
  sourceRevision: string; provenanceVerified: boolean; canonicalUrl: string;
  aliases: string[]; indexable: boolean;
};
export const CONTENT_MANIFEST = manifest as ContentMetadata[];
const sources = new Map(CONTENT_MANIFEST.map(doc => [doc.source, doc]));
const urls = new Map(CONTENT_MANIFEST.map(doc => [doc.url, doc]));
const aliases = new Map(CONTENT_MANIFEST.flatMap(doc => doc.aliases.map(alias => [alias, doc.url] as const)));
for (const [alias, destination] of Object.entries(DOCUMENT_ALIASES)) aliases.set(alias, destination);
export const contentBySource = (source: string) => sources.get(SOURCE_ALIASES[source] || source);
export const contentByUrl = (url: string) => urls.get(aliases.get(url) || url);
export function readingDestination(url: string): string {
  const path = url.split(/[?#]/)[0];
  const doc = contentByUrl(path);
  return doc && !doc.ready ? doc.canonicalUrl : url;
}
export function resolveDocumentAlias(url: string): string | undefined {
  const clean = url.replace(/\/$/, '') || '/';
  const direct = aliases.get(clean) || aliases.get(clean.replace(/\.html?$/i, '.md'));
  if (direct) return direct;
  const withoutExtension = clean.replace(/\.(md|json)$/, '');
  return aliases.get(withoutExtension);
}
function normalizePath(path: string): string {
  const parts: string[] = [];
  for (const part of path.split('/')) {
    if (part === '..') parts.pop();
    else if (part && part !== '.') parts.push(part);
  }
  return parts.join('/');
}
/** Resolve upstream markdown/RFC paths to registered routes; retain real source links otherwise. */
export function resolveContentHref(href: string, source: string): string {
  if (!href || /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(href)) return href;
  const match = href.match(/^([^?#]*)(.*)$/);
  if (!match) return href;
  const [, path, suffix] = match;
  const rfc = path.match(/(?:^|\/)rfcs(?:-txt)?\/([^/]+?)(?:\.(?:md|txt|html?))?\/?$/);
  if (rfc) return rfcSpecUrl(rfc[1]) + suffix;
  const rootPath = (path.startsWith('/') ? path.slice(1) : normalizePath(source.slice(0, source.lastIndexOf('/') + 1) + path)).replace(/\.html?$/i, '.md');
  const renderedRelative = normalizePath(source.replace(/\.[^/.]+$/, '') + '/' + path);
  const candidates = [rootPath, `${rootPath.replace(/\/$/, '')}.md`, `${rootPath.replace(/\/$/, '')}/index.md`, `${renderedRelative.replace(/\/$/, '')}.md`, `${renderedRelative.replace(/\/$/, '')}/index.md`];
  for (const candidate of candidates) {
    const doc = contentBySource(candidate);
    if (doc) return readingDestination(doc.url) + suffix;
  }
  const publicPath = '/' + rootPath.replace(/\/$/, '').replace(/\.(md|json)$/, '');
  const alias = resolveDocumentAlias(publicPath);
  if (alias) return readingDestination(alias) + suffix;
  if (path.startsWith('/') && !/\.(md|json|html?)$/i.test(path)) return readingDestination(href);
  if (/\.(md|json|html?)$/i.test(path)) {
    const upstream = /^(schemas|specifications)\//.test(rootPath) ? rootPath : `docs/${rootPath}`;
    return `${REPO_URL}/blob/main/${upstream}${suffix}`;
  }
  return publicPath + suffix;
}
